"use client"

import { useEffect, useState, useRef } from "react"
import { Check, ArrowRight, ChevronDown, ChevronUp, CreditCard, Shield, Lock, Zap } from "lucide-react"
import { getGateways } from "../utils/api"
import { motion, useAnimation, useInView, AnimatePresence } from "framer-motion"
import { useLocation, useNavigate } from "react-router-dom"
import ModernSidebar from "./admin_sidebar"
import ModernHeader from "./modern-header"
import { isGuest } from "../utils/isGuest"

export default function PaymentGateways() {
  const [gateways, setGateways] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedCards, setExpandedCards] = useState({})
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [fullName, setFullName] = useState("")
  const [email, setEmail] = useState("")
  const location = useLocation()
  const controls = useAnimation()
  const navigate=useNavigate()
  const isGuestValid=isGuest()

  const INITIAL_FEATURES_COUNT = 3

  const toggleSidebar = () => setSidebarOpen((prev) => !prev)

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load gateways
  useEffect(() => {
    const fetchGateways = async () => {
      try {
        setLoading(true)
        const data = await getGateways()
        // Filter only active gateways and add styling
        const activeGateways = data
          .filter(gateway => gateway.isActive)
          .map(gateway => ({
            ...gateway,
            style: "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-2xl"
          }))
        setGateways(activeGateways)
      } catch (err) {
        console.error(err)
        setError("Failed to load payment gateways")
      } finally {
        setLoading(false)
      }
    }

    fetchGateways()
  }, [])

  const toggleCardExpansion = (gatewayId) =>
    setExpandedCards((prev) => ({ ...prev, [gatewayId]: !prev[gatewayId] }))

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <div className="inline-block px-6 py-4 bg-red-50 border border-red-200 rounded-lg text-red-700 font-medium">
          {error}
        </div>
      </div>
    )
  }

  const gridCols =
    gateways.length === 1
      ? "grid-cols-1 justify-items-center"
      : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <ModernSidebar
        isOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        isMobile={isMobile}
        fullName={fullName}
        email={email}
      />
      <div
        className={`flex flex-col flex-1 transition-all duration-300 ${isMobile ? "ml-0" : sidebarOpen ? "ml-16 sm:ml-18 md:ml-20 lg:ml-20 xl:ml-20" : "ml-0"}`}
      >
        <ModernHeader toggleSidebar={toggleSidebar} isMobile={isMobile} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="px-6 space-y-8">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-black via-gray-900 to-slate-600 bg-clip-text text-transparent">
                  Payment Gateways
                </h1>
                <p className="text-gray-600 mt-2">Available payment methods for your transactions</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-2 shadow-sm">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Secure Payments</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Gateway Cards */}
            <div className={`grid ${gridCols} gap-8 max-w-6xl mx-auto`}>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-2xl shadow-xl border-2 border-gray-100 p-6 w-full max-w-md mx-auto"
                  >
                    <div className="space-y-4">
                      <div className="text-center space-y-3">
                        <div className="h-6 bg-gray-200 rounded-lg w-3/4 mx-auto animate-pulse"></div>
                        <div className="h-10 bg-gray-200 rounded-lg w-1/2 mx-auto animate-pulse"></div>
                      </div>
                      <div className="space-y-2">
                        {[1, 2, 3].map((item) => (
                          <div
                            key={item}
                            className="flex items-center space-x-3"
                          >
                            <div className="w-4 h-4 bg-gray-200 rounded-full animate-pulse"></div>
                            <div className="h-3 bg-gray-200 rounded flex-1 animate-pulse"></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                gateways.map((gateway) => {
                  const isExpanded = expandedCards[gateway._id]
                  const visibleFeatures = gateway.features.slice(
                    0,
                    INITIAL_FEATURES_COUNT
                  )
                  const hasMoreFeatures =
                    gateway.features.length > INITIAL_FEATURES_COUNT

                  return (
                    <motion.div
                      key={gateway._id}
                      className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md mx-auto relative overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300"
                      animate={controls}
                      variants={cardVariants}
                    >
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-purple-600"></div>
                      <div className="absolute top-3 right-3 w-16 h-16 bg-gradient-to-br from-blue-600/5 to-purple-600/5 rounded-full blur-xl"></div>

                      <div className="relative z-10">
                        <div className="text-center mb-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {gateway.name}
                          </h3>
                          <div className="flex items-center justify-center gap-2">
                            <Shield className="w-4 h-4 text-green-500" />
                            <span className="text-sm text-green-600">Secure Payment</span>
                          </div>
                        </div>

                        {gateway.link && (
                          <a
                            href={isGuestValid?'/login':gateway.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full mb-6 px-4 py-3 rounded-xl font-bold text-sm bg-black text-white hover:from-blue-700 hover:to-purple-700 shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
                          >
                            <span>Avail Now</span>
                            <ArrowRight className="w-4 h-4" />
                          </a>
                        )}

                        <div className="space-y-3">
                          <h4 className="font-bold text-gray-900 text-sm mb-3 border-b border-gray-100 pb-1">
                            Key Features:
                          </h4>
                          <ul className="space-y-2">
                            {visibleFeatures.map((feature, idx) => (
                              <li
                                key={idx}
                                className="flex items-start gap-2"
                              >
                                <div className="flex-shrink-0 w-4 h-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mt-0.5">
                                  <Check size={10} className="text-white" />
                                </div>
                                <span className="text-gray-800 text-sm leading-relaxed">
                                  {feature}
                                </span>
                              </li>
                            ))}
                          </ul>

                          <AnimatePresence>
                            {isExpanded && (
                              <motion.ul
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-2 overflow-hidden"
                              >
                                {gateway.features
                                  .slice(INITIAL_FEATURES_COUNT)
                                  .map((feature, idx) => (
                                    <li
                                      key={idx + INITIAL_FEATURES_COUNT}
                                      className="flex items-start gap-2"
                                    >
                                      <div className="flex-shrink-0 w-4 h-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mt-0.5">
                                        <Check
                                          size={10}
                                          className="text-white"
                                        />
                                      </div>
                                      <span className="text-gray-800 text-sm leading-relaxed">
                                        {feature}
                                      </span>
                                    </li>
                                  ))}
                              </motion.ul>
                            )}
                          </AnimatePresence>

                          {hasMoreFeatures && (
                            <button
                              onClick={() => toggleCardExpansion(gateway._id)}
                              className="w-full mt-3 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 hover:text-white transition-all duration-300 flex items-center justify-center gap-1"
                            >
                              <span>
                                {isExpanded
                                  ? "View Less"
                                  : `View More (${gateway.features.length -
                                  INITIAL_FEATURES_COUNT
                                  } more)`}
                              </span>
                              {isExpanded ? (
                                <ChevronUp size={14} />
                              ) : (
                                <ChevronDown size={14} />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )
                })
              )}
            </div>

            {/* Footer Section */}
            <motion.div
              className="text-center mt-12"
              initial="hidden"
              animate={controls}
              variants={cardVariants}
            >
              <div className="flex flex-col items-center justify-center bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mb-4">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Secure Payment Processing</h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  All our payment gateways use industry-standard encryption and security protocols to ensure your transactions are safe and protected.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-6 mt-6">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Instant Processing</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-purple-600" />
                    <span className="font-medium">256-bit Encryption</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">PCI DSS Compliant</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </main>
      </div>
    </div>
  )
}